import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface NavigationContextType {
  canGoBack: boolean;
  canGoForward: boolean;
  goBack: () => void;
  goForward: () => void;
  breadcrumbs: BreadcrumbItem[];
  currentPageTitle: string;
  isDashboardPage: boolean;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

/**
 * Routes that are considered "dashboard pages" and get the new navigation
 */
const DASHBOARD_ROUTES = [
  '/dashboard',
  '/admin-dashboard',
  '/coach-dashboard',
  '/team-information',
  '/athlete/',
  '/settings',
];

/**
 * Check if current route is a dashboard page
 */
const isDashboardRoute = (pathname: string): boolean => {
  return DASHBOARD_ROUTES.some(route => 
    pathname === route || pathname.startsWith(route)
  );
};

/**
 * Words that should always be uppercase (acronyms)
 */
const ALWAYS_UPPERCASE = new Set([
  'ai', 'api', 'hrv', 'rhr', 'id', 'ui', 'ux', 'oauth', 'jwt'
]);

/**
 * Words that should be lowercase in titles (unless first word)
 */
const LOWERCASE_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in',
  'into', 'of', 'on', 'or', 'the', 'to', 'with'
]);

/**
 * Convert path segment to human-readable format
 */
const formatSegment = (segment: string): string => {
  const words = segment.split(/[-_]+/);
  
  return words.map((word, index) => {
    const lowerWord = word.toLowerCase();
    
    if (ALWAYS_UPPERCASE.has(lowerWord)) {
      return word.toUpperCase();
    }
    
    if (index > 0 && LOWERCASE_WORDS.has(lowerWord)) {
      return lowerWord;
    }
    
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
};

/**
 * Convert route path to human-readable label
 */
const pathToLabel = (pathname: string): string => {
  if (pathname.startsWith('/athlete/')) {
    return 'Athlete Detail';
  }
  
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (segments.length === 0) return 'Dashboard';
  
  const mainSegment = segments[segments.length - 1];
  return formatSegment(mainSegment);
};

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [history, setHistory] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const isDashboardPage = isDashboardRoute(location.pathname);

  useEffect(() => {
    if (!isDashboardPage) return;

    // Only track pathname changes, not tab/view parameter changes
    // Tab changes are handled by TabNavigation, not browser history
    const pathname = location.pathname;
    
    if (history[currentIndex] !== pathname) {
      const newHistory = [...history.slice(0, currentIndex + 1), pathname];
      setHistory(newHistory);
      setCurrentIndex(newHistory.length - 1);
    }
  }, [location.pathname, isDashboardPage]);

  const canGoBack = isDashboardPage && currentIndex > 0;
  const canGoForward = isDashboardPage && currentIndex < history.length - 1;

  const goBack = () => {
    if (canGoBack) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      navigate(history[newIndex]);
    }
  };

  const goForward = () => {
    if (canGoForward) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      navigate(history[newIndex]);
    }
  };

  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    if (!isDashboardPage) return [];

    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const crumbs: BreadcrumbItem[] = [];
    
    const baseLabel = pathToLabel(path);
    crumbs.push({ label: baseLabel, path });
    
    const tab = searchParams.get('tab');
    const view = searchParams.get('view');
    const subSection = tab || view;
    
    if (subSection) {
      const subLabel = formatSegment(subSection);
      const paramKey = tab ? 'tab' : 'view';
      crumbs.push({ 
        label: subLabel, 
        path: `${path}?${paramKey}=${subSection}` 
      });
    }
    
    return crumbs;
  };

  const breadcrumbs = buildBreadcrumbs();
  const currentPageTitle = breadcrumbs[breadcrumbs.length - 1]?.label || 'Dashboard';

  return (
    <NavigationContext.Provider value={{
      canGoBack,
      canGoForward,
      goBack,
      goForward,
      breadcrumbs,
      currentPageTitle,
      isDashboardPage,
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

